/**
 * Module-level kit context + helpers so CmsImage / observers can emit events
 * without prop-drilling. Updated whenever a media kit mounts or switches slug.
 *
 * Also dual-emits photo_impression / photo_open so /admin/fotos-analytics
 * (photo_public_id + worker_country) includes Media Kit traffic.
 */
import type { MediaKitTrackContext } from "@/lib/posthogAnalytics";
import {
  trackMediaKitImageLoadPostHog,
  trackMediaKitImageImpressionPostHog,
} from "@/lib/posthogAnalytics";
import { trackPhotoImpression, trackPhotoOpen } from "@/lib/photoTracking";

let currentCtx: MediaKitTrackContext | null = null;

export function setMediaKitAnalyticsRuntime(ctx: MediaKitTrackContext | null) {
  currentCtx = ctx;
}

export function getMediaKitAnalyticsRuntime(): MediaKitTrackContext | null {
  return currentCtx;
}

export function countMediaKitImages(content: {
  hero?: { image?: { publicId?: string } };
  about?: { image?: { publicId?: string } };
  visualWorld?: { images?: Array<{ publicId?: string }> };
  campaigns?: { concepts?: Array<{ image?: { publicId?: string } }> };
}): number {
  let n = 0;
  if (content.hero?.image?.publicId) n += 1;
  if (content.about?.image?.publicId) n += 1;
  n += content.visualWorld?.images?.length ?? 0;
  n += content.campaigns?.concepts?.length ?? 0;
  return n;
}

function resourceTiming(url: string): {
  load_ms: number | null;
  transfer_size: number | null;
  encoded_body_size: number | null;
} {
  try {
    const entries = performance.getEntriesByName(url) as PerformanceResourceTiming[];
    const e = entries[entries.length - 1];
    if (!e) {
      return { load_ms: null, transfer_size: null, encoded_body_size: null };
    }
    return {
      load_ms: Math.round(e.duration || e.responseEnd - e.startTime),
      transfer_size:
        typeof e.transferSize === "number" ? e.transferSize : null,
      encoded_body_size:
        typeof e.encodedBodySize === "number" ? e.encodedBodySize : null,
    };
  } catch {
    return { load_ms: null, transfer_size: null, encoded_body_size: null };
  }
}

export type MkImageTrackMeta = {
  publicId: string;
  variant?: string;
  role?: string;
  alt?: string;
  sectionId?: string;
  sectionType?: string;
  loadStartedAt: number;
};

/** Call from <img onLoad / onError> for Cloudinary delivery metrics. */
export function reportMediaKitImageLoad(
  meta: MkImageTrackMeta,
  img: HTMLImageElement | null,
  success: boolean,
  errorMessage?: string,
) {
  const ctx = currentCtx;
  if (!ctx || !meta.publicId) return;

  const src = img?.currentSrc || img?.src || "";
  let srcHost: string | null = null;
  try {
    if (src) srcHost = new URL(src, window.location.origin).hostname;
  } catch {
    srcHost = null;
  }

  const timing = src ? resourceTiming(src) : {
    load_ms: null,
    transfer_size: null,
    encoded_body_size: null,
  };
  const wallMs = Math.round(performance.now() - meta.loadStartedAt);

  trackMediaKitImageLoadPostHog({
    ...ctx,
    public_id: meta.publicId,
    image_variant: meta.variant,
    image_role: meta.role,
    section_id: meta.sectionId,
    section_type: meta.sectionType,
    success,
    load_ms: timing.load_ms ?? wallMs,
    natural_width: img?.naturalWidth || null,
    natural_height: img?.naturalHeight || null,
    transfer_size: timing.transfer_size,
    encoded_body_size: timing.encoded_body_size,
    src_host: srcHost,
    error_message: errorMessage ?? null,
  });
}

/** Impression when image crosses viewport threshold (dedupe outside). */
export function reportMediaKitImageImpression(
  meta: Omit<MkImageTrackMeta, "loadStartedAt"> & {
    intersection_ratio?: number;
    src?: string;
  },
) {
  const ctx = currentCtx;
  if (!ctx || !meta.publicId) return;

  trackMediaKitImageImpressionPostHog({
    ...ctx,
    public_id: meta.publicId,
    image_variant: meta.variant,
    image_role: meta.role,
    section_id: meta.sectionId,
    section_type: meta.sectionType,
    alt: meta.alt,
    intersection_ratio: meta.intersection_ratio,
  });

  // Same catalog as home gallery → admin fotos-analytics by country
  const src =
    meta.src ||
    `https://res.cloudinary.com/x/image/upload/${meta.publicId}`;
  trackPhotoImpression(src, undefined, {
    section: `media_kit_${meta.role || meta.variant || "image"}`,
    templateType: "MediaKit",
    publicId: meta.publicId,
    openSource: "media_kit",
    extra: {
      kit_slug: ctx.kit_slug,
      kit_title: ctx.kit_title,
      media_kit_section_id: meta.sectionId ?? null,
      media_kit_section_type: meta.sectionType ?? null,
      image_role: meta.role ?? null,
      image_variant: meta.variant ?? null,
    },
  });
}

/** User clicked / “opened” a media kit image (lightbox-less expand intent). */
export function reportMediaKitImageOpen(
  meta: Omit<MkImageTrackMeta, "loadStartedAt"> & { src?: string },
) {
  const ctx = currentCtx;
  if (!ctx || !meta.publicId) return;

  const src =
    meta.src ||
    `https://res.cloudinary.com/x/image/upload/${meta.publicId}`;
  trackPhotoOpen(src, undefined, {
    section: `media_kit_${meta.role || meta.variant || "image"}`,
    templateType: "MediaKit",
    publicId: meta.publicId,
    openSource: "media_kit",
    extra: {
      kit_slug: ctx.kit_slug,
      kit_title: ctx.kit_title,
      media_kit_section_id: meta.sectionId ?? null,
      media_kit_section_type: meta.sectionType ?? null,
      image_role: meta.role ?? null,
      image_variant: meta.variant ?? null,
    },
  });
}
